import styled from "@emotion/styled";
import { noop } from "lodash";
import { useState } from "react";
import * as React from "react";

import { DesignToken, Loader, SpacingToken, Text, Tooltip } from "@livechat/design-system-react-components";

import { Icon } from "components/icon";
import { IWithTestSelector } from "components/interfaces";
import { withVar } from "themes/helpers";

interface IStyledIconContainerProps {
  disabled: boolean;
  active: boolean;
  privateMode: boolean;
}

export const StyledIconContainer = styled.div<IStyledIconContainerProps>`
  align-items: center;
  cursor: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => (props.disabled ? "default" : "pointer")
  };
  display: inline-flex;
  height: 24px;
  justify-content: center;
  width: 24px;
  border-radius: 4px;
  border: 1px transparent solid;
  background-color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => (props.active ? withVar(DesignToken.ActionPrimaryDefault) : "transparent")
  };
  line-height: 24px;

  &:hover,
  &:active {
    ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) =>
        !props.active &&
        !props.disabled &&
        `background-color: ${withVar(
          props.privateMode ? DesignToken.SurfaceInvertPrimary : DesignToken.SurfacePrimaryHover
        )};`
    }

    svg {
      ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) =>
          !props.disabled &&
          `fill: ${withVar(props.privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)}`
      };
      ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) => props.active && `fill: ${withVar(DesignToken.ContentInvertPrimary)}`
      };
    }

    span {
      ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) =>
          !props.disabled &&
          `color: ${withVar(props.privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)}`
      }
    }
  }

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.disabled && "opacity: 0.6;"
  }

  span {
    color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) =>
        withVar(props.privateMode ? DesignToken.ContentInvertSecondary : DesignToken.ContentBasicSecondary)
    };
  }

  svg {
    fill: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) =>
        withVar(props.privateMode ? DesignToken.ContentInvertSecondary : DesignToken.ContentBasicSecondary)
    };
    ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.active && `fill: ${withVar(DesignToken.ContentInvertPrimary)};`
    }
  }

  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    height: 24px;
    width: 24px;
  }
`;

const IconTooltipWrapper = styled.div`
  margin-right: ${withVar(SpacingToken.Spacing1)};
  position: relative;

  .lc-css-tooltip {
    white-space: nowrap;
    padding: 6px 8px;
  }

  &:hover .lc-css-tooltip,
  &:focus .lc-css-tooltip,
  &:active .lc-css-tooltip,
  &:focus-within .lc-css-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

export interface IToolbarIconProps extends IWithTestSelector {
  icon: string | React.ReactNode;
  disabled: boolean;
  privateMode: boolean;
  mobile: boolean;
  active?: boolean;
  customSize?: string;
  tooltip?: string;
  isLoading?: boolean;
  setShowTip?(v: boolean): void;
  onMouseDown(e: React.MouseEvent<HTMLDivElement>): void;
}

const ToolbarIcon: React.FC<IToolbarIconProps> = ({
  icon,
  disabled,
  privateMode,
  mobile,
  customSize,
  active = false,
  isLoading = false,
  tooltip = null,
  setShowTip,
  onMouseDown,
  testSelector,
}) => {
  const size = "18px";
  const fill = "inherit";
  const iconComponent =
    typeof icon === "string" ? (
      <Icon name={icon} width={customSize || size} height={customSize || size} fill={fill} inline />
    ) : (
      icon
    );
  const [showTooltip, setShowTooltip] = useState(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleMouseHover = (value: boolean) => {
    if (typeof setShowTip === "function") {
      setShowTip(value);
    }
    setShowTooltip(value);
  };

  return (
    <IconTooltipWrapper>
      <Tooltip
        kind="invert"
        isVisible={Boolean(tooltip && !mobile && !disabled && showTooltip)}
        triggerRenderer={
          <StyledIconContainer
            data-testid={testSelector}
            disabled={disabled}
            privateMode={privateMode}
            active={active}
            onMouseDown={disabled ? noop : onMouseDown}
            onMouseLeave={() => handleMouseHover(false)}
            onMouseEnter={() => handleMouseHover(true)}
            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick={(ev: any) => {
              ev.preventDefault();
              ev.stopPropagation();
              ev.nativeEvent.stopImmediatePropagation();
            }}
          >
            {isLoading ? <Loader size="small" /> : iconComponent}
          </StyledIconContainer>
        }
        placement="top"
      >
        <Text css={{ textWrap: "nowrap" }}>{tooltip}</Text>
      </Tooltip>
    </IconTooltipWrapper>
  );
};

export default ToolbarIcon;
